import { Dispatch, SetStateAction, useState } from "react";

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

type StateFields<T extends Record<string, any>> = {
    [K in keyof T]: T[K];
};

type Setters<T extends Record<string, any>> = {
    [K in keyof T as `set${Capitalize<string & K>}`]: Dispatch<SetStateAction<T[K]>>;
};

/** Type for the reactive state. See `useReactiveState` for more details. */
export type ReactiveState<T extends Record<string, any>> = StateFields<T> &
    Setters<T> & {
        getValues: () => T;
    };

/**
 * A hook that returns a reactive state object.
 * The type of the state can be any object with string keys.
 * The returned object has all the fields of the initial state object,
 * as well as setter functions for each field, and a `getValues` function
 * that returns the current state object.
 *
 * A reasoned use case for this hook is when you have the state of a child
 * component in its parent (e.g. when conditional rendering happens),
 * and you want to pass the state and the setter functions to the child.
 *
 * @example
 * const formState = useReactiveState({ username: "", password: "" });
 *
 * if (condition) {
 *  return <ChildComponent state={formState} />;
 * } else {
 *  return <AnotherComponent />;
 * }
 * */
export function useReactiveState<T extends Record<string, any>>(initialState: T): ReactiveState<T> {
    const [state, setState] = useState(initialState);

    // Created once, like the setters of `useState`: the state object itself is new on every
    // render, so a hook depending on it would re-run each time - depend on a setter instead.
    const [setters] = useState(() =>
        Object.keys(initialState).reduce((acc, key) => {
            acc[`set${capitalize(key)}`] = (value: any | ((prev: any) => any)) => {
                setState((prev) => ({ ...prev, [key]: typeof value === "function" ? value(prev[key]) : value }));
            };
            return acc;
        }, {} as Setters<T>),
    );

    return { ...state, ...setters, getValues: () => state };
}
